import { useState } from "react";
import { useRouter } from "next/router";

import AuthWrapper from "@/components/auth/auth-wrapper";
import SigInWithOtp from "@/components/auth/signin/SignInWithOtp";
import Otp from "@/components/otp";
import Loader from "@/components/loader";
import { useDispatch } from "react-redux";

import successToast, { errorToast } from "@/utils/toast";
import { updateCartItemCount } from "@/store/feature/cartSlice";
import { updateWishlistItemCount } from "@/store/feature/wishlistSlice";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import getGRecaptchaToken from "@/services/googleReCaptcha";
import NewUserForm from "@/components/action/form/NewUserForm";
import OtpError from "@/components/data-display/card/OtpError";

import { useOtpVerify, useLoginByPhone } from "@/api/client/auth";
import { useGetNotification } from "@/api/client/notification";
import { removeGuestUser } from "@/services/guest-user";

import { signIn } from "next-auth/react";
import { getSession } from "next-auth/react";
import * as fbq from "lib/fpixel";
import * as gtm from "lib/gtm";
import { createJwtToken } from "@/services/jsonwebtoken";


function SignInWithMobilePhone() {
  const router = useRouter();
  const dispatch = useDispatch();
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState("");
  const [signInSignature, setSignInSignature] = useState("");
  const [otpErrorMessage, setOtpErrorMessage] = useState("");
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [currentStep, setCurrentStep] = useState("SIGN_IN");

  const { refetch: fetchNotification } = useGetNotification({
    enabled: false,
    onSuccess: (notification) => {
      dispatch(updateCartItemCount(notification.cart_product_count));
      dispatch(updateWishlistItemCount(notification.wishlist_product_count));
    },
  });

  const { mutate: verifyOtp } = useOtpVerify({
    onSuccess: async (successData) => {
      setOtpErrorMessage("");

      if (successData.data.new_customer) {
        setCurrentStep("NEW_USER");
        setCustomerPhoneNumber(successData.data.phone);
        setSignInSignature(successData.data.reg_signature);

        fbq.signUpEvent("mobile")
        gtm.signUpEvent("mobile")
        return;
      }

      const authRes = await signIn("credentials", {
        redirect: false,
        user: JSON.stringify(successData.data.customer),
        token: successData.data.token,
      });
      if (authRes.ok) {
        fetchNotification();
        removeGuestUser();
        fbq.signInEvent("mobile")
        gtm.signInEvent("mobile")
        router.push(router.query.from || router.query.callbackUrl || "/");
      }
    },
    onError: (errorData) => {
      setOtpErrorMessage(errorData?.errors[0]?.message);
    },
  });

  const { isLoading: isResendOtpLoading, mutate: loginByPhone } =
    useLoginByPhone({
      onSuccess: (successData) => {
        successToast(successData.message);
        setSignInSignature(successData.data.signature);
      },
      onError: (errorData) => {
        errorToast(errorData?.message);
      },
    });

  const handleOtpSend = (data) => {
    setCurrentStep("OTP");
    setCustomerPhoneNumber(data.phone);
    setSignInSignature(data.signature);
  };

  const onResendOtpRequest = async () => {
    loginByPhone({
      phone: customerPhoneNumber,
      gRecaptchaToken: await getGRecaptchaToken(executeRecaptcha, "resendOtp"),
      token: createJwtToken(),
    });
  };

  const RenderedComponent = {
    SIGN_IN: <SigInWithOtp onSubmit={handleOtpSend} />,
    OTP: (
      <Otp
        phoneNumber={customerPhoneNumber}
        onResendOtpRequest={onResendOtpRequest}
        onOtpSubmit={(otp) =>
          verifyOtp({
            phone: customerPhoneNumber,
            otp,
            signature: signInSignature,
          })
        }
        onPhoneNumberChangeRequest={() => {
          setOtpErrorMessage("");
          setCurrentStep("SIGN_IN");
        }}
      />
    ),
    NEW_USER: (
      <NewUserForm phone={customerPhoneNumber} signature={signInSignature} />
    ),
  };

  return (
    <>
      {isResendOtpLoading && <Loader />}

      <AuthWrapper imageUrl="https://apn081-amarpet-prod.sgp1.cdn.digitaloceanspaces.com/assets/signin-banner.webp">
        <div>{RenderedComponent[currentStep]}</div>
        {otpErrorMessage && <OtpError errorMessage={otpErrorMessage} />}
      </AuthWrapper>
    </>
  );
}

export const getServerSideProps = async ({ req }) => {
  const session = await getSession({ req });

  if (session?.token) {
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
    };
  }

  return {
    props: { session },
  };
};

export default SignInWithMobilePhone;
